import React, { useState, useEffect, useRef } from 'react';
import placeService from '../services/placeService';
import HamburgerMenu from '../components/HamburgerMenu';
import PlaceCard from '../components/PlaceCard';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MainPage.css';

const API_BASE_URL = 'https://fifty-two-in-the-lou-production.up.railway.app';

const GUEST_VISITED_KEY = 'guest_visited_places';

const getGuestVisited = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GUEST_VISITED_KEY) || '[]');
  } catch { return []; }
};

const setGuestVisited = (ids) => {
  sessionStorage.setItem(GUEST_VISITED_KEY, JSON.stringify(ids));
};

const CATEGORY_COLORS = {
  'Music': '#8B5CF6',
  'Food & Beverage': '#F59E0B',
  'Nature': '#10B981',
  'Events & Shows': '#EC4899',
  'Sports': '#3B82F6',
  'Landmarks': '#CC0000',
  'Other': '#6B7280',
};

const tintColor = (hex, amount = 0.5) => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.round((num >> 16) + (255 - (num >> 16)) * amount);
  const g = Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount);
  const b = Math.round((num & 0xff) + (255 - (num & 0xff)) * amount);
  return `rgb(${r},${g},${b})`;
};

const getEdgeColor = (place, isVisited) => {
  if (isVisited) return '#000000';
  const baseColor = CATEGORY_COLORS[place.category] || '#6B7280';
  if (place.userAdded) return tintColor(baseColor, 0.5);
  const isBottom8 = place.id >= 53 && place.id <= 60;
  if (isBottom8) return tintColor(baseColor, 0.5);
  return baseColor;
};

const sortPlaces = (data, sortByCategory = false) => {
  return [...data].sort((a, b) => {
    const aVisited = a.status === 'visited';
    const bVisited = b.status === 'visited';
    if (aVisited !== bVisited) return aVisited ? 1 : -1;
    if (aVisited && bVisited) {
      const aTime = a.checkedAt ? new Date(a.checkedAt).getTime() : 0;
      const bTime = b.checkedAt ? new Date(b.checkedAt).getTime() : 0;
      return aTime - bTime;
    }
    if (sortByCategory) {
      const catCompare = (a.category || '').localeCompare(b.category || '');
      if (catCompare !== 0) return catCompare;
    }
    const aScheduled = !!a.scheduledDate;
    const bScheduled = !!b.scheduledDate;
    const aUserAdded = !!a.userAdded;
    const bUserAdded = !!b.userAdded;
    if (aScheduled && bScheduled) return new Date(a.scheduledDate) - new Date(b.scheduledDate);
    if (aScheduled) return -1;
    if (bScheduled) return 1;
    if (aUserAdded && bUserAdded) return (a.category || '').localeCompare(b.category || '');
    if (aUserAdded) return -1;
    if (bUserAdded) return 1;
    return a.id - b.id;
  });
};

const SwipeableRow = ({ children, onDelete, isUserAdded }) => {
  const [swipeX, setSwipeX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const startX = useRef(null);
  const THRESHOLD = 80;

  const handleTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!startX.current || !isUserAdded) return;
    const diff = e.touches[0].clientX - startX.current;
    if (diff < 0) setSwipeX(Math.max(diff, -120));
  };

  const handleTouchEnd = () => {
    if (swipeX < -THRESHOLD) {
      setSwipeX(-120);
    } else {
      setSwipeX(0);
    }
    setIsSwiping(false);
    startX.current = null;
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '10px' }}>
      {isUserAdded && (
        <div style={{
          position: 'absolute', right: 0, top: 0, bottom: 0,
          width: '120px', backgroundColor: '#ef4444',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '0 10px 10px 0', cursor: 'pointer'
        }} onClick={onDelete}>
          <span style={{ color: 'white', fontWeight: '700', fontSize: '0.85rem' }}>🗑️ Delete</span>
        </div>
      )}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: isSwiping ? 'none' : 'transform 0.3s ease',
          position: 'relative', zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
};

const MainPage = () => {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [sortByCategory, setSortByCategory] = useState(false);
  const { theme, backgroundImage, backgroundSize } = useTheme();
  const { user, getToken } = useAuth();
  const { isGuest, exitGuestMode } = useGuest();
  const navigate = useNavigate();

  const guestBlocked = (msg = 'Create a free account to unlock this feature!') => {
    if (isGuest) {
      alert(msg);
      return true;
    }
    return false;
  };

  const fetchPlaces = React.useCallback(async (status = '', category = '', myPlacesOnly = false, sortByCat = false) => {
    try {
      setSortByCategory(sortByCat);
      if (isGuest) {
        const res = await fetch(`${API_BASE_URL}/api/guest/places`);
        const data = await res.json();
        const visited = getGuestVisited();
        const merged = data.map(p =>
          visited.includes(p.id)
            ? { ...p, status: 'visited', checkedAt: new Date().toISOString() }
            : p
        );
        setPlaces(sortPlaces(merged, sortByCat));
      } else {
        const token = getToken();
        const data = await placeService.getAllPlaces(token, status, category, myPlacesOnly);
        setPlaces(sortPlaces(data, sortByCat));
      }
    } catch (err) {
      console.error('Failed to fetch places', err);
    } finally {
      setLoading(false);
    }
  }, [user, isGuest, getToken]);

  useEffect(() => { fetchPlaces(); }, [fetchPlaces]);

  const handleToggle = async (id) => {
    if (isGuest) {
      const visited = getGuestVisited();
      const already = visited.includes(id);
      const updated = already ? visited.filter(v => v !== id) : [...visited, id];
      setGuestVisited(updated);
      setPlaces(prev => {
        const updatedPlaces = prev.map(p =>
          p.id === id ? { ...p, status: already ? 'unvisited' : 'visited', checkedAt: already ? null : new Date().toISOString() } : p
        );
        return sortPlaces(updatedPlaces, sortByCategory);
      });
      return;
    }
    const token = getToken();
    await placeService.togglePlace(token, id);
    const current = await placeService.getAllPlaces(token, '', '', false);
    setPlaces(sortPlaces(current, sortByCategory));
  };

  const handleGuestReset = () => {
    if (window.confirm('Are you sure you want to reset? This will uncheck all places.')) {
      sessionStorage.removeItem(GUEST_VISITED_KEY);
      fetchPlaces();
    }
  };

  const handleReset = async () => {
    if (isGuest) {
      handleGuestReset();
      return;
    }
    if (window.confirm('Are you sure you want to reset your checklist?')) {
      try {
        const token = getToken();
        await axios.post(`${API_BASE_URL}/api/checklist/reset-all`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchPlaces();
      } catch (e) {
        console.error('Reset failed', e);
        alert('Reset failed. Please try again.');
      }
    }
  };
  
  const handleRestore = async () => {
    try {
      const token = getToken();
      await placeService.restoreUserPlaces(token);
      fetchPlaces();
    } catch (e) {
      console.error('Restore failed', e);
      alert('Restore failed. Please try again.');
    }
  };

  const handleSchedule = async (id, scheduledDate) => {
    if (guestBlocked('Sign up free to schedule places!')) return;
    const token = getToken();
    await placeService.schedulePlace(token, id, scheduledDate);
    fetchPlaces();
  };

  const handleDeleteSchedule = async (id) => {
    if (guestBlocked()) return;
    const token = getToken();
    await placeService.deleteSchedule(token, id);
    fetchPlaces();
    setSelectedPlace(null);
  };

  const handleDeleteUserPlace = async (id) => {
    if (guestBlocked()) return;
    try {
      const token = getToken();
      await placeService.deleteUserPlace(token, id);
      fetchPlaces();
      setSelectedPlace(null);
    } catch (e) {
      console.error('Failed to delete user place', e);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const token = getToken();
      await axios.post(`${API_BASE_URL}/api/checklist/delete-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPlaces();
    } catch (e) {
      console.error('Delete all failed', e);
      alert('Delete all failed. Please try again.');
    }
  };

  const handleSignUp = () => {
    exitGuestMode();
    navigate('/login', { state: { showRegister: true } });
  };

  const bannerBg = theme.headerBackground;
  const bannerText = theme.headerText;

  return (
    <div className="main-container" style={{
      backgroundColor: theme.background,
      backgroundImage: backgroundImage,
      backgroundSize: backgroundSize,
      backgroundRepeat: 'repeat',
      backgroundBlendMode: 'overlay',
      opacity: 1,
    }}>
      {selectedPlace && (
        <PlaceCard
          place={selectedPlace}
          onClose={() => setSelectedPlace(null)}
          onToggle={handleToggle}
          onSchedule={handleSchedule}
          onDeleteSchedule={handleDeleteSchedule}
          onDeletePlace={handleDeleteUserPlace}
          isGuest={isGuest}
        />
      )}

      {isGuest && (
        <div style={{
          background: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: '#ffffff',
          textAlign: 'center',
          padding: '10px 16px',
          fontWeight: '700',
          fontSize: '14px',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderRadius: '0 0 12px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
        }}>
          Guest mode — previewing 55 of 60 places.{' '}
          <span onClick={handleSignUp} style={{ textDecoration: 'underline', cursor: 'pointer', color: '#EEC170' }}>
            Sign up free
          </span>{' '}
          to unlock everything!
        </div>
      )}

      <header className="main-header" style={{
        backgroundColor: theme.headerBackground,
        marginTop: isGuest ? '44px' : '0'
      }}>
        <HamburgerMenu
          onFilterChange={(status, category, myPlacesOnly, sortByCat) => {
            if (isGuest) {
              fetchPlaces('', category, false, sortByCat);
            } else {
              fetchPlaces(status, category, myPlacesOnly, sortByCat);
            }
          }}
          onReset={handleReset}
          onRestore={handleRestore}
          onPlaceAdded={isGuest ? () => guestBlocked('Sign up free to add your own places!') : fetchPlaces}
          onDeleteAll={handleDeleteAll}
          isGuest={isGuest}
        />
        <img
          src="/logos/52-logo-horiz.png"
          alt="52 in the Lou"
          style={{ height: '40px', objectFit: 'contain' }}
        />
        <div className="header-spacer" />
      </header>

      <div className="checklist-wrapper">
        <div className="checklist-container">
          {loading ? (
            <p className="loading-text" style={{ color: theme.subText }}>Loading...</p>
          ) : (
            places.map((place) => {
              const isVisited = place.status === 'visited';
              const isUserAdded = !!place.userAdded;
              const categoryColor = CATEGORY_COLORS[place.category] || '#6B7280';
              const edgeColor = getEdgeColor(place, isVisited);
              const tintedColor = tintColor(categoryColor, 0.5);

              return (
                <SwipeableRow
                  key={`${isUserAdded ? 'user' : 'official'}-${place.id}`}
                  isUserAdded={isUserAdded && !isGuest}
                  onDelete={() => handleDeleteUserPlace(place.id)}
                >
                  <div
                    className="checklist-item"
                    style={{
                      backgroundColor: theme.cardBackground,
                      borderLeftColor: edgeColor,
                      borderLeftWidth: '4px',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isVisited}
                      onChange={() => handleToggle(place.id)}
                      className="checklist-checkbox"
                      style={{ accentColor: isUserAdded ? tintedColor : categoryColor }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="checklist-text" style={{ flex: 1 }}>
                      <span className="checklist-title" style={{
                        color: isUserAdded ? theme.subText : theme.text,
                        textDecoration: isVisited ? 'line-through' : 'none'
                      }}>
                        {place.title}
                      </span>
                      <span className="checklist-category" style={{
                        color: isUserAdded ? tintedColor : categoryColor
                      }}>
                        {place.category}
                      </span>
                      {place.scheduledDate && (
                        <span style={{ color: isVisited ? '#ef4444' : '#22c55e', fontSize: '0.75rem', fontWeight: '700' }}>
                          📅 {new Date(place.scheduledDate).toLocaleString([], {
                            month: 'numeric', day: 'numeric', year: '2-digit',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedPlace(place)}
                      style={{
                        background: 'none',
                        border: `1px solid ${theme.border}`,
                        borderRadius: '6px',
                        padding: '4px 8px',
                        color: theme.subText,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      View More
                    </button>
                  </div>
                </SwipeableRow>
              );
            })
          )}
        </div>
      </div>

      <footer style={{
        textAlign: 'center',
        padding: '20px',
        color: theme.subText,
        fontSize: '0.8rem',
        marginTop: 'auto'
      }}>
        JWMP © 2026
      </footer>
    </div>
  );
};

export default MainPage;

// working